// Player settings and personal bests in localStorage (same keys as the prototype).
// Every access is wrapped: storage can be missing or throw (private mode, blocked site data).

import { AIRACCEL_PRESETS } from '../sim/constants';
import { defaultKeybinds, sanitizeKeybinds, type Keybinds } from '../input/keybinds';
import { defaultEditorBinds, sanitizeEditorBinds, type EditorBinds } from '../editor/editorKeys';
import { FINISHES, type Finish } from '../render/finishes';
import { isKnifeKind, type KnifeKind } from '../render/knives';
import { STATIONS, type Station } from '../audio/music';
import { defaultCrosshair, sanitizeCrosshair, type CrosshairSettings } from '../ui/crosshair';

export interface Settings {
  /** CS sensitivity; degrees per count = sens x 0.022 (m_yaw). */
  sens: number;
  airaccel: number;
  autobhop: boolean;
  /** Slide down slopes after a fall (not CS:S). */
  slopeSlide: boolean;
  /** Horizontal FOV at 4:3, like CS. */
  fov: number;
  knife: boolean;
  /** Show the replay bot (the map's record run). */
  replayBot: boolean;
  /** Knife blade finish. */
  finish: Finish;
  /** Which knife you hold. */
  knifeType: KnifeKind;
  /** Viewmodel camera vertical FOV in degrees. */
  viewmodelFov: number;
  leftHanded: boolean;
  /** Show the CS:S knife and USP (from the player's game files) when available. */
  cssModels: boolean;
  /** Sound volume 0..100. */
  volume: number;
  /** Music volume 0..100 (built-in maps). */
  musicVolume: number;
  /** Music muted (the mute key). */
  musicMuted: boolean;
  /** Touch screens: strafe automatically while turning in the air. */
  touchAssist: boolean;
  /** Touch screens: swipe look speed, 1 = 0.25 degrees per pixel. */
  touchLook: number;
  /** Which Audius genre station plays. */
  musicStation: Station;
  /** Go fullscreen when playing, so Chrome/Edge hand Ctrl+W and similar shortcuts to the game. */
  fullscreen: boolean;
  /** Upper limit for devicePixelRatio. Lower it for more FPS on high-DPI screens. */
  maxPixelRatio: number;
  /**
   * Low-latency canvas (desynchronized): frames go to the screen without waiting on the browser's
   * page compositing, which on some PCs shows a frame one refresh late now and then. May tear.
   */
  lowLatency: boolean;
  debug: boolean;
  binds: Keybinds;
  /** Level builder controls. */
  editorBinds: EditorBinds;
  crosshair: CrosshairSettings;
  /** Settings format version (migrations). */
  v: number;
}

const KEY = 'bhop.settings';

export function defaultSettings(): Settings {
  return {
    sens: 2.0,
    airaccel: 1337,
    autobhop: true,
    slopeSlide: true,
    fov: 90,
    knife: true,
    replayBot: true,
    finish: 'fireice',
    knifeType: 'karambit',
    viewmodelFov: 54,
    leftHanded: false,
    cssModels: true,
    volume: 50,
    musicVolume: 10,
    musicMuted: true,
    touchAssist: true,
    touchLook: 1,
    musicStation: 'mix',
    fullscreen: true,
    maxPixelRatio: 2,
    lowLatency: false,
    debug: false,
    binds: defaultKeybinds(),
    editorBinds: defaultEditorBinds(),
    crosshair: defaultCrosshair(),
    v: 4,
  };
}

export function loadSettings(): Settings {
  const s = defaultSettings();
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || 'null') as Partial<Settings> | null;
    if (raw && typeof raw === 'object') {
      if (typeof raw.sens === 'number' && raw.sens > 0) s.sens = raw.sens;
      if (typeof raw.airaccel === 'number' && (AIRACCEL_PRESETS as readonly number[]).includes(raw.airaccel))
        s.airaccel = raw.airaccel;
      if (typeof raw.autobhop === 'boolean') s.autobhop = raw.autobhop;
      if (typeof raw.slopeSlide === 'boolean') s.slopeSlide = raw.slopeSlide;
      if (typeof raw.fov === 'number' && raw.fov >= 75 && raw.fov <= 110) s.fov = raw.fov;
      if (typeof raw.knife === 'boolean') s.knife = raw.knife;
      if (typeof raw.replayBot === 'boolean') s.replayBot = raw.replayBot;
      if (typeof raw.finish === 'string' && raw.finish in FINISHES) s.finish = raw.finish;
      if (isKnifeKind(raw.knifeType)) s.knifeType = raw.knifeType;
      if (typeof raw.viewmodelFov === 'number' && raw.viewmodelFov >= 40 && raw.viewmodelFov <= 80)
        s.viewmodelFov = raw.viewmodelFov;
      if (typeof raw.leftHanded === 'boolean') s.leftHanded = raw.leftHanded;
      if (typeof raw.cssModels === 'boolean') s.cssModels = raw.cssModels;
      if (typeof raw.volume === 'number' && raw.volume >= 0 && raw.volume <= 100) s.volume = raw.volume;
      if (typeof raw.musicVolume === 'number' && raw.musicVolume >= 0 && raw.musicVolume <= 100)
        s.musicVolume = raw.musicVolume;
      if (typeof raw.musicMuted === 'boolean') s.musicMuted = raw.musicMuted;
      if (typeof raw.touchAssist === 'boolean') s.touchAssist = raw.touchAssist;
      if (typeof raw.touchLook === 'number' && raw.touchLook >= 0.25 && raw.touchLook <= 4)
        s.touchLook = raw.touchLook;
      if (STATIONS.some((x) => x.id === raw.musicStation)) s.musicStation = raw.musicStation as Station;
      if (typeof raw.fullscreen === 'boolean') s.fullscreen = raw.fullscreen;
      if (typeof raw.maxPixelRatio === 'number' && raw.maxPixelRatio > 0) s.maxPixelRatio = raw.maxPixelRatio;
      if (typeof raw.lowLatency === 'boolean') s.lowLatency = raw.lowLatency;
      if (typeof raw.debug === 'boolean') s.debug = raw.debug;
      s.binds = sanitizeKeybinds(raw.binds);
      s.editorBinds = sanitizeEditorBinds(raw.editorBinds);
      s.crosshair = sanitizeCrosshair(raw.crosshair);
      // v2: sv_airaccelerate 1337 became the default (and replaced the 1000 preset).
      if (!raw.v || raw.v < 2) s.airaccel = 1337;
      // v3: music starts quiet (10%) and muted; M turns it on.
      if (!raw.v || raw.v < 3) {
        s.musicVolume = 10;
        s.musicMuted = true;
      }
      // v4: changing skin moved from O to P (if P is free and O was still the key).
      if (!raw.v || raw.v < 4) {
        const b = s.binds.randomFinish;
        const pUsed = Object.values(s.binds).some((codes) => codes.includes('KeyP'));
        if (b.length === 1 && b[0] === 'KeyO' && !pUsed) s.binds.randomFinish = ['KeyP'];
      }
    }
  } catch {
    // Defaults.
  }
  return s;
}

export function saveSettings(s: Settings) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Not persisted.
  }
}

/** PBs are per sv_airaccelerate + autobhop setting, and per map for loaded maps. */
const pbKey = (s: Settings, map?: string) =>
  (map ? 'bhop.pb.map.' + map.toLowerCase() + '.aa' : 'bhop.pb.aa') +
  s.airaccel +
  (s.autobhop ? '.auto' : '.legit');

/** Personal best in seconds for the current settings (and map, if given), or null. */
export function getPB(s: Settings, map?: string): number | null {
  try {
    const v = localStorage.getItem(pbKey(s, map));
    return v ? parseFloat(v) : null;
  } catch {
    return null;
  }
}

export function setPB(s: Settings, t: number, map?: string) {
  try {
    localStorage.setItem(pbKey(s, map), String(t));
  } catch {
    // Not persisted.
  }
}

/** Stage split times (seconds) of the personal best run, same keys as the PB. */
export function getSplits(s: Settings, map?: string): number[] | null {
  try {
    const v = localStorage.getItem(pbKey(s, map) + '.splits');
    const a = v ? (JSON.parse(v) as unknown) : null;
    return Array.isArray(a) && a.every((x) => typeof x === 'number') ? a : null;
  } catch {
    return null;
  }
}

export function setSplits(s: Settings, splits: number[], map?: string) {
  try {
    localStorage.setItem(pbKey(s, map) + '.splits', JSON.stringify(splits));
  } catch {
    // Not persisted.
  }
}

export function getFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function setFlag(key: string) {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // Not persisted.
  }
}
